import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  RegenerativePath,
  RegenerativePathValidationFlags,
} from '../models/regenerative-path.model';
import { RegenerativePathCreate } from '../models/regenerative-path-create.model';
import { CropService } from '../../../common/_services';
import { Crop, RegenerativeProtocol } from '../../../common/_models';

/**
 * @description Modernized service to manage Regenerative Paths.
 * Uses Signals and async-safe methods instead of BehaviorSubjects for most use cases.
 */
@Injectable({
  providedIn: 'root',
})
export class RegenerativePathService {
  private regenerativeService = environment.regenerativeServiceUrl;

  private http = inject(HttpClient);
  private cropService = inject(CropService);

  // Reactive state signals
  currentRegenerativePath = signal<RegenerativePath | null>(null);
  regenerativePaths = signal<RegenerativePath[] | null>(null);
  // The selected protocol in the path creation flow
  selectedProtocolInPathCreation: RegenerativeProtocol = null;
  // Variable that holds info about the path being created
  public currentRegenerativePathCreate = new BehaviorSubject<RegenerativePathCreate>(
    new RegenerativePathCreate()
  );
  // Boolean that manage the buttons for the stepper in the path creation flow
  public isContinueDisabled: boolean = true;

  //? In a new path creation, user needs visibility to only the crops in the baseline year
  public cropsInBaselineYear: Crop[] = [];

  /**
   * @description Set the currently active regenerative path.
   * @param path The path to set
   */
  setCurrentRegenerativePath(path: RegenerativePath | null) {
    this.currentRegenerativePath.set(path ? Object.assign(new RegenerativePath(), path) : null);
  }

  /**
   * @description Get all regenerative paths from the API.
   * @returns Promise containing all paths and total count
   */
  async getRegenerativePaths(): Promise<{ data: RegenerativePath[]; totalCount: number } | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ data: RegenerativePath[]; totalCount: number }>(
          `${this.regenerativeService}api/private/v1/paths`
        )
      );
      this.regenerativePaths.set(response.data);
      return response;
    } catch (error) {
      console.error('Failed to fetch regenerative paths:', error);
      return null;
    }
  }

  /**
   * @description Get a specific regenerative path by ID.
   * @param pathId The path ID
   * @returns Promise resolving to the path or null if not found
   */
  async getPathById(pathId: string): Promise<RegenerativePath | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ data: RegenerativePath[]; totalCount: number }>(
          `${this.regenerativeService}api/private/v1/paths/${pathId}`
        )
      );
      const path = response.data[0] ?? null;
      if (path) this.setCurrentRegenerativePath(path);
      return path;
    } catch (error) {
      console.error(`Failed to fetch path by ID ${pathId}:`, error);
      return null;
    }
  }

  /**
   * @description Get regenerative paths filtered by protocol ID
   * @param protocolId The protocol ID
   * @param allCompanies Whether to include all companies
   * @returns Promise resolving to the paths or null on error
   */
  async getPathsByProtocolId(
    protocolId: string,
    allCompanies: boolean = false
  ): Promise<RegenerativePath[] | null> {
    try {
      const params = new URLSearchParams({ protocolId });
      if (allCompanies) params.append('all', 'true');

      const response = await firstValueFrom(
        this.http.get<{ data: RegenerativePath[]; totalCount: number }>(
          `${this.regenerativeService}api/private/v1/paths?${params.toString()}`
        )
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch paths by protocol ${protocolId}:`, error);
      return null;
    }
  }

  /**
   * @description Create a new regenerative path
   * @param path Path creation payload
   * @returns Created path or null on error
   */
  async createPath(path: RegenerativePathCreate): Promise<RegenerativePath | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<{ data: RegenerativePath[]; totalCount: number }>(
          `${this.regenerativeService}api/private/v1/paths`,
          path
        )
      );
      return response.data[0] ?? null;
    } catch (error) {
      console.error('Failed to create path:', error);
      return null;
    }
  }

  /**
   * @description Update an existing regenerative path
   * @param pathId Path ID
   * @param path Updated path data
   * @returns Updated path or null on error
   */
  async updatePath(pathId: string, path: RegenerativePath): Promise<RegenerativePath | null> {
    try {
      const response = await firstValueFrom(
        this.http.put<{ data: RegenerativePath; totalCount: number }>(
          `${this.regenerativeService}api/private/v1/paths/${pathId}`,
          path
        )
      );
      return response.data ?? null;
    } catch (error) {
      console.error(`Failed to update path ${pathId}:`, error);
      return null;
    }
  }

  /**
   * @description Delete a regenerative path by ID
   * @param pathId Path ID
   * @returns True if successful, false otherwise
   */
  async deletePath(pathId: string): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.delete(`${this.regenerativeService}api/private/v1/paths/${pathId}`)
      );
      return true;
    } catch (error) {
      console.error(`Failed to delete path ${pathId}:`, error);
      return false;
    }
  }

  /**
   * @description Update the validation flags of a regenerative path
   * @param pathId Path ID
   * @param flagsObj Validation flags
   * @returns Updated path or null on error
   */
  async updatePathValidationFlags(
    pathId: string,
    flagsObj: RegenerativePathValidationFlags
  ): Promise<RegenerativePath | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<{ data: RegenerativePath; totalCount: number }>(
          `${this.regenerativeService}api/private/v1/paths/updateFlagPathValidation/${pathId}`,
          flagsObj
        )
      );
      const updated = response.data ?? null;
      if (updated) this.setCurrentRegenerativePath(updated);
      return updated;
    } catch (error) {
      console.error(`Failed to update validation flags for path ${pathId}:`, error);
      return null;
    }
  }

  /**
   * @description Retrieve crops related to the current regenerative path
   * @returns Promise resolving to crops for the current path
   */
  async getCurrentRegenerativePathCrops(): Promise<Crop[]> {
    const path = this.currentRegenerativePath();
    if (!path) return [];

    const fieldIds = new Set(path.sections.map((s) => s.fieldId));
    const parcelIds = new Set(path.sections.map((s) => s.parcelId).filter(Boolean));

    try {
      const crops = await firstValueFrom(
        this.cropService.getCrops(undefined, undefined, path.baseYear)
      );
      return crops.filter((c) => fieldIds.has(c.field?.id) || parcelIds.has(c.parcel?.id));
    } catch (error) {
      console.error('Failed to fetch crops for current path:', error);
      return [];
    }
  }

  /**
   * @description Ensures current path is loaded, fetching from API if needed
   */
  async ensureCurrentRegenerativePath(pathId?: string): Promise<RegenerativePath | null> {
    if (this.currentRegenerativePath()) return this.currentRegenerativePath();
    if (!pathId) return null;
    return this.getPathById(pathId);
  }
}
